import React, { useEffect, useState } from "react";
import { Row, Col, Card, CardBody, Container, Badge, Nav, NavItem, NavLink, TabContent, TabPane, CardHeader } from "reactstrap";
import EventService from "../../../services/KangarooServices/EventService";
import { useNavigate, useParams } from "react-router-dom";
import { imageBaseApi } from "../../../utilis/imageHelper";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { Calendar, CheckCircle, Edit3, ExternalLink, FileText, ImageIcon, Info, LinkIcon, MapPin, Phone, Tag, User, XCircle } from "lucide-react";
import { formatDate, formatDateTime } from "../../../Components/Common/FormatDate";

const EventDetail = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
    const [activeMainTab, setActiveMainTab] = useState("1");

  const navigate = useNavigate()
  const fetchEventDetail = async (id) => {
    try {
      setLoading(true);
      const response = await EventService.getDetail(id);
      setData(response);
    } catch (err) {
      console.error("Failed to fetch event detail:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchEventDetail(id);
    }
  }, [id]);

  const toggleMainTab = (tab) => {
    if (activeMainTab !== tab) setActiveMainTab(tab);
  };
  // console.log(data?.bannerMedia?.url);

    const breadcrumbItems = [{ title: "Back to List", link: "/event" }];


  return (
   <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Event Detail"
          pageTitle="Event"
          breadcrumbItems={breadcrumbItems}
        />

        {/* Hero Section with Banner */}
        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-sm mb-4 overflow-hidden">
              {data?.bannerMedia?.url ? (
                <div className="position-relative" style={{ backgroundColor: "#1e293b" }}>
                  <img
                    src={`${imageBaseApi}${data.bannerMedia.url}`}
                    alt={data?.name}
                    className="w-100"
                    style={{ 
                      height: "300px", 
                      objectFit: "cover",
                      opacity: "0.6"
                    }}
                  />
                  <div className="position-absolute top-0 end-0 m-3 m-md-4">
                    <button
                      type="button"
                      className="btn btn-light btn-sm d-flex align-items-center gap-2 shadow"
                      onClick={() => navigate(`/event/update/${id}`)}
                    >
                      <Edit3 size={14} />
                      <span className="d-none d-sm-inline">Update Event</span>
                    </button>
                  </div>
                  <div className="position-absolute bottom-0 start-0 end-0 p-3 p-md-4 text-white">
                    <div className="d-flex flex-column gap-2">
                      <Badge
                        color="primary"
                        className="px-3 py-2 fs-6 align-self-start d-inline-flex align-items-center gap-1"
                      >
                        <Tag size={14} />
                        <span className="text-truncate" style={{ maxWidth: "200px" }}>
                          {data?.eventType}
                        </span>
                      </Badge>
                      <h1 className="display-5 fw-bold mb-0 text-white text-break pe-5">
                        {data?.name}
                      </h1>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-primary bg-gradient bg-opacity-10">
                  <CardBody className="p-3 p-md-4">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3">
                      <div className="flex-grow-1 w-100 overflow-hidden">
                        <Badge color="primary" className="mb-3 px-3 py-2 fs-6 d-inline-flex align-items-center gap-1">
                          <Tag size={14} />
                          <span className="text-truncate d-inline-block" style={{ maxWidth: "250px" }}>
                            {data?.eventType}
                          </span>
                        </Badge>
                        <h1 className="display-5 fw-bold mb-0 text-break text-primary">
                          {data?.name}
                        </h1>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm d-flex align-items-center gap-2 flex-shrink-0"
                        onClick={() => navigate(`/event/update/${id}`)}
                      >
                        <Edit3 size={14} />
                        <span>Update</span>
                      </button>
                    </div>
                  </CardBody>
                </div>
              )}
            </Card>
          </Col>
        </Row>

        {/* Main Tabs */}
        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-sm">
              <CardBody className="p-0">
                {/* Main Tab Navigation */}
                <Nav tabs className="nav-tabs-custom border-bottom-0">
                  <NavItem>
                    <NavLink
                      className={`${activeMainTab === "1" ? "active" : ""} py-3 px-4 d-flex align-items-center gap-2`}
                      onClick={() => toggleMainTab("1")}
                      style={{ cursor: "pointer" }}
                    >
                      <Info size={18} />
                      <span className="fw-semibold">Event Information</span>
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={`${activeMainTab === "2" ? "active" : ""} py-3 px-4 d-flex align-items-center gap-2`}
                      onClick={() => toggleMainTab("2")}
                      style={{ cursor: "pointer" }}
                    >
                      <MapPin size={18} />
                      <span className="fw-semibold d-flex align-items-center gap-2">
                        Cities & Locations
                        {data?.eventsCities && data.eventsCities.length > 0 && (
                          <Badge color="primary" className="ms-1 rounded-pill">
                            {data.eventsCities.length}
                          </Badge>
                        )}
                      </span>
                    </NavLink>
                  </NavItem>
                </Nav>

                {/* Main Tab Content */}
                <TabContent activeTab={activeMainTab}>
                  {/* Tab 1: Event Information */}
                  <TabPane tabId="1">
                    <div className="p-4">
                      <Row>
                        {/* Main Content */}
                        <Col lg={8}>
                          {/* Event Date & Time */}
                          <Card className="border shadow-sm mb-4">
                            <CardHeader className="bg-light border-bottom">
                              <h5 className="mb-0 d-flex align-items-center gap-2 text-primary">
                                <Calendar size={20} />
                                <span className="fw-bold">Event Schedule</span>
                              </h5>
                            </CardHeader>
                            <CardBody className="p-4">
                              <Row className="g-4">
                                <Col md={6}>
                                  <div className="border-start border-success border-4 ps-3 py-2">
                                    <div className="text-muted small mb-2 text-uppercase fw-semibold">
                                      Start Date & Time
                                    </div>
                                    <div className="fw-bold text-success fs-6">
                                      {formatDate(data?.startDate)}
                                    </div>
                                  </div>
                                </Col>
                                <Col md={6}>
                                  <div className="border-start border-danger border-4 ps-3 py-2">
                                    <div className="text-muted small mb-2 text-uppercase fw-semibold">
                                      End Date & Time
                                    </div>
                                    <div className="fw-bold text-danger fs-6">
                                      {formatDate(data?.endDate)}
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                            </CardBody>
                          </Card>

                          {/* Event Description */}
                          <Card className="border shadow-sm mb-4">
                            <CardHeader className="bg-light border-bottom">
                              <h5 className="mb-0 d-flex align-items-center gap-2 text-primary">
                                <FileText size={20} />
                                <span className="fw-bold">Event Description</span>
                              </h5>
                            </CardHeader>
                            <CardBody className="p-4">
                              <p className="fs-6 lh-lg text-secondary mb-0">
                                {data?.description}
                              </p>
                            </CardBody>
                          </Card>

                          {/* Event Details Grid */}
                          <Card className="border shadow-sm">
                            <CardHeader className="bg-light border-bottom">
                              <h5 className="mb-0 d-flex align-items-center gap-2 text-primary">
                                <Info size={20} />
                                <span className="fw-bold">Additional Information</span>
                              </h5>
                            </CardHeader>
                            <CardBody className="p-4">
                              <Row className="g-4">
                                <Col md={6}>
                                  <div className="d-flex align-items-start gap-3 p-3 bg-light rounded">
                                    <div className="bg-primary bg-opacity-10 rounded-3 p-3 flex-shrink-0">
                                      <MapPin size={22} className="text-primary" />
                                    </div>
                                    <div className="flex-grow-1 overflow-hidden">
                                      <div className="text-muted small mb-1 text-uppercase fw-semibold">
                                        Venue
                                      </div>
                                      <div className="fw-bold text-break text-dark">
                                        {data?.venue}
                                      </div>
                                    </div>
                                  </div>
                                </Col>

                                <Col md={6}>
                                  <div className="d-flex align-items-start gap-3 p-3 bg-light rounded">
                                    <div className="bg-success bg-opacity-10 rounded-3 p-3 flex-shrink-0">
                                      <User size={22} className="text-success" />
                                    </div>
                                    <div className="flex-grow-1 overflow-hidden">
                                      <div className="text-muted small mb-1 text-uppercase fw-semibold">
                                        Speaker
                                      </div>
                                      <div className="fw-bold text-break text-dark">
                                        {data?.speaker}
                                      </div>
                                    </div>
                                  </div>
                                </Col>

                                <Col md={6}>
                                  <div className="d-flex align-items-start gap-3 p-3 bg-light rounded">
                                    <div className="bg-warning bg-opacity-10 rounded-3 p-3 flex-shrink-0">
                                      <Tag size={22} className="text-warning" />
                                    </div>
                                    <div className="flex-grow-1">
                                      <div className="text-muted small mb-2 text-uppercase fw-semibold">
                                        Event Type
                                      </div>
                                      <Badge color="primary" className="px-3 py-2 fs-6 fw-semibold">
                                        {data?.eventType}
                                      </Badge>
                                    </div>
                                  </div>
                                </Col>

                                <Col md={6}>
                                  <div className="d-flex align-items-start gap-3 p-3 bg-light rounded">
                                    <div
                                      className={`${
                                        data?.registrationRequired
                                          ? "bg-success"
                                          : "bg-danger"
                                      } bg-opacity-10 rounded-3 p-3 flex-shrink-0`}
                                    >
                                      {data?.registrationRequired ? (
                                        <CheckCircle size={22} className="text-success" />
                                      ) : (
                                        <XCircle size={22} className="text-danger" />
                                      )}
                                    </div>
                                    <div className="flex-grow-1">
                                      <div className="text-muted small mb-2 text-uppercase fw-semibold">
                                        Registration Required
                                      </div>
                                      <Badge
                                        color={data?.registrationRequired ? "success" : "danger"}
                                        className="px-3 py-2 fs-6 fw-semibold"
                                      >
                                        {data?.registrationRequired ? "Yes" : "No"}
                                      </Badge>
                                    </div>
                                  </div>
                                </Col>

                                {data?.registrationUrl && (
                                  <Col md={12}>
                                    <div className="d-flex align-items-start gap-3 p-3 bg-light rounded">
                                      <div className="bg-info bg-opacity-10 rounded-3 p-3 flex-shrink-0">
                                        <ExternalLink size={22} className="text-info" />
                                      </div>
                                      <div className="flex-grow-1 overflow-hidden">
                                        <div className="text-muted small mb-1 text-uppercase fw-semibold">
                                          Registration URL
                                        </div>
                                        <a
                                          href={
                                            data.registrationUrl.startsWith("http")
                                              ? data.registrationUrl
                                              : `https://${data.registrationUrl}`
                                          }
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-primary fw-bold text-decoration-none d-inline-flex align-items-center gap-2"
                                        >
                                          <span className="text-break">Register Now</span>
                                          <ExternalLink size={16} className="flex-shrink-0" />
                                        </a>
                                      </div>
                                    </div>
                                  </Col>
                                )}

                                {data?.slug && (
                                  <Col md={12}>
                                    <div className="d-flex align-items-start gap-3 p-3 bg-light rounded">
                                      <div className="bg-secondary bg-opacity-10 rounded-3 p-3 flex-shrink-0">
                                        <LinkIcon size={22} className="text-secondary" />
                                      </div>
                                      <div className="flex-grow-1 overflow-hidden">
                                        <div className="text-muted small mb-1 text-uppercase fw-semibold">
                                          Event Slug
                                        </div>
                                        <code className="text-dark bg-white px-2 py-1 rounded border">
                                          {data.slug}
                                        </code>
                                      </div>
                                    </div>
                                  </Col>
                                )}
                              </Row>
                            </CardBody>
                          </Card>
                        </Col>

                        {/* Sidebar */}
                        <Col lg={4}>
                          {/* Banner Image Card */}
                          <Card className="border shadow-sm mb-4 overflow-hidden">
                            <CardHeader className="bg-light border-bottom">
                              <h5 className="mb-0 d-flex align-items-center gap-2 text-primary">
                                <ImageIcon size={20} />
                                <span className="fw-bold">Event Banner</span>
                              </h5>
                            </CardHeader>
                            <CardBody className="p-0">
                              {data?.thumbnailMedia?.url ? (
                                <>
                                  <div className="bg-light d-flex justify-content-center align-items-center" style={{ minHeight: "250px" }}>
                                    <img
                                      src={`${imageBaseApi}${data.thumbnailMedia.url}`}
                                      alt={data?.name}
                                      className="img-fluid w-100"
                                      style={{
                                        maxHeight: "300px",
                                        objectFit: "contain",
                                      }}
                                    />
                                  </div>
                                  {data.thumbnailMedia.fileName && (
                                    <div className="p-3 bg-white border-top">
                                      <div className="d-flex align-items-center gap-2 text-muted small">
                                        <Tag size={14} className="text-primary flex-shrink-0" />
                                        <span className="text-truncate fw-semibold">
                                          {data.thumbnailMedia.fileName}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div
                                  className="d-flex flex-column justify-content-center align-items-center text-center bg-light p-5"
                                  style={{ minHeight: "250px" }}
                                >
                                  <ImageIcon size={56} className="text-muted mb-3 opacity-50" />
                                  <h6 className="mb-2 text-muted fw-bold">No Banner Image</h6>
                                  <p className="small text-muted mb-0">
                                    Banner image not uploaded for this event
                                  </p>
                                </div>
                              )}
                            </CardBody>
                          </Card>

                          {/* Target Country */}
                          {data?.targetCountry && (
                            <Card className="border shadow-sm mb-4">
                              <CardHeader className="bg-light border-bottom">
                                <h5 className="mb-0 d-flex align-items-center gap-2 text-primary">
                                  <Globe size={20} />
                                  <span className="fw-bold">Target Country</span>
                                </h5>
                              </CardHeader>
                              <CardBody className="p-4">
                                <Badge color="info" className="mb-3 px-3 py-2 fs-6 fw-semibold">
                                  {data.targetCountry.name}
                                </Badge>
                                {data.targetCountry.description && (
                                  <div
                                    className="text-muted small lh-base"
                                    dangerouslySetInnerHTML={{
                                      __html: data.targetCountry.description,
                                    }}
                                  />
                                )}
                              </CardBody>
                            </Card>
                          )}
                        </Col>
                      </Row>
                    </div>
                  </TabPane>

                  {/* Tab 2: Cities & Locations */}
                  <TabPane tabId="2">
                    <div className="p-4">
                      {data?.eventsCities && data.eventsCities.length > 0 ? (
                        <Row className="g-4">
                          {data.eventsCities.map((city, index) => (
                            <Col lg={3} md={6} key={index}>
                              <Card className="border shadow-sm h-100 overflow-hidden">
                                {city.eventImage && (
                                  <div className="position-relative" style={{ height: "200px", overflow: "hidden" }}>
                                    <img
                                      src={`${imageBaseApi}${city.eventImage.url}`}
                                      alt={city.city}
                                      className="w-100 h-100"
                                      style={{ objectFit: "cover" }}
                                    />
                                    <div 
                                      className="position-absolute bottom-0 start-0 end-0 p-3"
                                      style={{
                                        background: "linear-gradient(to top, rgba(0,0,0,0.8), transparent)"
                                      }}
                                    >
                                      <h4 className="mb-0 fw-bold text-white">
                                        <MapPin size={18} className="me-2" />
                                        {city.city}
                                      </h4>
                                    </div>
                                  </div>
                                )}

                                <CardBody className="p-4">
                                  {!city.eventImage && (
                                    <h4 className="mb-3 fw-bold text-primary border-bottom border-primary border-3 pb-2 d-inline-flex align-items-center gap-2">
                                      <MapPin size={20} />
                                      {city.city}
                                    </h4>
                                  )}

                                  <div className="d-flex flex-column gap-3">
                                    {city.eventDate && (
                                      <div className="d-flex align-items-start gap-3">
                                        <div className="bg-success bg-opacity-10 rounded-3 p-2 flex-shrink-0">
                                          <Calendar size={18} className="text-success" />
                                        </div>
                                        <div className="flex-grow-1">
                                          <div className="text-muted small mb-1 text-uppercase" style={{ fontSize: "0.7rem", fontWeight: "600" }}>
                                            Event Date
                                          </div>
                                          <div className="fw-semibold text-dark small">
                                            {formatDateTime(city.eventDate)}
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    <div className="d-flex align-items-start gap-3">
                                      <div className="bg-info bg-opacity-10 rounded-3 p-2 flex-shrink-0">
                                        <MapPin size={18} className="text-info" />
                                      </div>
                                      <div className="flex-grow-1">
                                        <div className="text-muted small mb-1 text-uppercase" style={{ fontSize: "0.7rem", fontWeight: "600" }}>
                                          Location
                                        </div>
                                        <div className="fw-semibold text-dark small text-break">
                                          {city.location}
                                        </div>
                                      </div>
                                    </div>

                                    <div className="d-flex align-items-start gap-3">
                                      <div className="bg-warning bg-opacity-10 rounded-3 p-2 flex-shrink-0">
                                        <Phone size={18} className="text-warning" />
                                      </div>
                                      <div className="flex-grow-1">
                                        <div className="text-muted small mb-1 text-uppercase" style={{ fontSize: "0.7rem", fontWeight: "600" }}>
                                          Contact Phone
                                        </div>
                                        <a
                                          href={`tel:${city.phoneNumber}`}
                                          className="fw-semibold text-decoration-none d-inline-flex align-items-center gap-1 small text-dark"
                                        >
                                          <Phone size={14} />
                                          {city.phoneNumber}
                                        </a>
                                      </div>
                                    </div>
                                  </div>
                                </CardBody>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      ) : (
                        <div className="text-center py-5">
                          <div className="mb-4">
                            <MapPin size={64} className="text-muted opacity-50" />
                          </div>
                          <h4 className="text-muted fw-bold mb-2">No Event Cities Available</h4>
                          <p className="text-muted mb-0">
                            No additional event cities or locations have been added yet.
                          </p>
                        </div>
                      )}
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EventDetail;
